import type { MessageKey } from '@dijitalapp/i18n';
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from 'react';
import { useNavigate } from 'react-router';
import { useApp } from '../lib/app-context';
import type { CallManager, CallView } from '../lib/calls';
import { BACKGROUND_PRESETS, backgroundsSupported, presetBackground, removeCustomBackground, type BackgroundChoice, type BackgroundPreset } from '../lib/backgrounds';
import { effectFilter, effectsSupported, VIDEO_EFFECTS, type VideoEffect } from '../lib/video-effects';
import { Icon, type IconName } from './Icon';
import { UserAvatar } from './UserAvatar';

// The active call: a full screen with large controls, or a small floating window so the rest of the app stays
// usable (e.g. writing in the chat). Device, sound and effect settings open as panels over the call.

type SinkVideo = HTMLVideoElement & { setSinkId?: (id: string) => Promise<void> };

function CallVideo({
  stream,
  muted,
  className,
  sinkId,
  videoRef,
}: {
  stream: MediaStream | null;
  muted?: boolean;
  className: string;
  sinkId?: string | null;
  videoRef?: RefObject<HTMLVideoElement | null>;
}) {
  const own = useRef<HTMLVideoElement>(null);
  const ref = videoRef ?? own;
  useEffect(() => {
    if (ref.current && ref.current.srcObject !== stream) ref.current.srcObject = stream;
  }, [stream, ref]);
  useEffect(() => {
    const el = ref.current as SinkVideo | null;
    if (sinkId && el?.setSinkId) void el.setSinkId(sinkId).catch(() => undefined);
  }, [sinkId, ref]);
  return <video ref={ref} className={className} autoPlay playsInline muted={muted} />;
}

function RoundButton({ icon, label, onClick, active, danger, pressed }: { icon: IconName; label: string; onClick: () => void; active?: boolean; danger?: boolean; pressed?: boolean }) {
  return (
    <button
      type="button"
      className={`call-btn${active ? ' is-active' : ''}${danger ? ' is-danger' : ''}`}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      onClick={onClick}
    >
      <Icon name={icon} size={22} />
    </button>
  );
}

/** Live microphone level (only while the settings panel is open). */
function MicMeter({ stream }: { stream: MediaStream | null }) {
  const bar = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const track = stream?.getAudioTracks()[0];
    if (!track) return;
    const ctx = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(new MediaStream([track])).connect(analyser);
    const data = new Uint8Array(analyser.fftSize);
    let raf = 0;
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let peak = 0;
      for (const v of data) peak = Math.max(peak, Math.abs(v - 128));
      if (bar.current) bar.current.style.transform = `scaleX(${Math.min(1, peak / 64)})`;
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      void ctx.close();
    };
  }, [stream]);
  return (
    <span className="mic-meter" aria-hidden>
      <span ref={bar} />
    </span>
  );
}

function useDevices() {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  useEffect(() => {
    const load = () => void navigator.mediaDevices?.enumerateDevices().then(setDevices, () => undefined);
    load();
    navigator.mediaDevices?.addEventListener('devicechange', load);
    return () => navigator.mediaDevices?.removeEventListener('devicechange', load);
  }, []);
  return devices;
}

function DevicePanel({ call, calls, onClose }: { call: CallView; calls: CallManager; onClose: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const devices = useDevices();
  const [error, setError] = useState<string | null>(null);
  const s = calls.settings;
  const mics = devices.filter((d) => d.kind === 'audioinput');
  const cams = devices.filter((d) => d.kind === 'videoinput');
  const speakers = devices.filter((d) => d.kind === 'audiooutput');
  const canPickSpeaker = 'setSinkId' in HTMLMediaElement.prototype && speakers.length > 0;
  const currentMic = call.localStream?.getAudioTracks()[0]?.getSettings().deviceId ?? s.micId ?? '';
  const currentCam = calls.cameraTrack?.getSettings().deviceId ?? s.camId ?? '';
  const run = (fn: () => Promise<void> | void) => {
    setError(null);
    void Promise.resolve()
      .then(fn)
      .catch(() => setError(t('call.settings.failed')));
  };
  const label = (d: MediaDeviceInfo, i: number, key: MessageKey) => d.label || `${t(key)} ${i + 1}`;
  return (
    <div className="call-panel" role="dialog" aria-label={t('call.settings.title')}>
      <div className="call-panel-head">
        <strong>{t('call.settings.title')}</strong>
        <button type="button" className="call-panel-close" aria-label={t('common.close')} onClick={onClose}>
          <Icon name="close" size={18} />
        </button>
      </div>
      {mics.length > 0 && (
        <label className="call-field">
          <span>{t('call.settings.mic')}</span>
          <select className="input" value={currentMic} onChange={(e) => run(() => calls.setMicrophone(e.target.value))}>
            {mics.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                {label(d, i, 'call.settings.mic')}
              </option>
            ))}
          </select>
        </label>
      )}
      <MicMeter stream={call.localStream} />
      {call.media === 'video' && cams.length > 0 && (
        <label className="call-field">
          <span>{t('call.settings.camera')}</span>
          <select className="input" value={currentCam} onChange={(e) => run(() => calls.setCamera({ deviceId: e.target.value }))}>
            {cams.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                {label(d, i, 'call.settings.camera')}
              </option>
            ))}
          </select>
        </label>
      )}
      {canPickSpeaker && (
        <label className="call-field">
          <span>{t('call.settings.speaker')}</span>
          <select className="input" value={call.speakerId ?? 'default'} onChange={(e) => calls.setSpeaker(e.target.value)}>
            {speakers.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                {label(d, i, 'call.settings.speaker')}
              </option>
            ))}
          </select>
        </label>
      )}
      {(['noiseSuppression', 'echoCancellation', 'autoGainControl'] as const).map((k) => (
        <label key={k} className="check call-check">
          <input type="checkbox" checked={s[k]} onChange={(e) => run(() => calls.setAudioProcessing({ [k]: e.target.checked }))} />
          <span>{t(`call.settings.${k}`)}</span>
        </label>
      ))}
      {error && (
        <p className="call-panel-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function EffectsPanel({ call, calls, onClose }: { call: CallView; calls: CallManager; onClose: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  // Previews show the plain camera through each filter (cheap: CSS on the same stream).
  const preview = calls.cameraTrack ? new MediaStream([calls.cameraTrack]) : null;
  return (
    <div className="call-panel" role="dialog" aria-label={t('call.effects.title')}>
      <div className="call-panel-head">
        <strong>{t('call.effects.title')}</strong>
        <button type="button" className="call-panel-close" aria-label={t('common.close')} onClick={onClose}>
          <Icon name="close" size={18} />
        </button>
      </div>
      {!effectsSupported() ? (
        <p className="call-panel-note">{t('call.effects.unsupported')}</p>
      ) : (
        <div className="effect-grid" role="radiogroup" aria-label={t('call.effects.title')}>
          {VIDEO_EFFECTS.map((e: VideoEffect) => (
            <button key={e} type="button" role="radio" aria-checked={call.effect === e} className={`effect-tile${call.effect === e ? ' is-on' : ''}`} onClick={() => void calls.setEffect(e)}>
              <CallVideo stream={preview} muted className="effect-preview" />
              <span className="effect-swatch" style={{ backdropFilter: effectFilter(e) === 'none' ? undefined : effectFilter(e) }} />
              <span className="effect-name">{t(`call.effects.${e}`)}</span>
            </button>
          ))}
        </div>
      )}
      <BackgroundPicker call={call} calls={calls} />
      {/* The camera's own blur (some devices) is lighter on the battery than ours. */}
      {call.deviceBlur.available && (
        <label className="check call-check">
          <input type="checkbox" checked={call.deviceBlur.on} onChange={(e) => void calls.setDeviceBlur(e.target.checked)} />
          <span>{t('call.effects.deviceBlur')}</span>
        </label>
      )}
    </div>
  );
}

function PresetThumb({ preset }: { preset: BackgroundPreset }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let alive = true;
    void presetBackground(preset).then((bmp) => {
      const c = ref.current;
      if (alive && c) c.getContext('2d')?.drawImage(bmp, 0, 0, c.width, c.height);
      bmp.close();
    });
    return () => {
      alive = false;
    };
  }, [preset]);
  return <canvas ref={ref} width={128} height={72} className="bg-thumb-canvas" aria-hidden />;
}

/** Blur or replace what is behind you; your own picture stays on this device. */
function BackgroundPicker({ call, calls }: { call: CallView; calls: CallManager }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  if (!backgroundsSupported()) return <p className="call-panel-note">{t('call.bg.unsupported')}</p>;
  const pick = (choice: BackgroundChoice) => {
    setError(null);
    void calls.setBackground(choice);
  };
  const tile = (choice: BackgroundChoice, label: string, content: ReactNode) => (
    <button key={choice} type="button" role="radio" aria-checked={call.background === choice} className={`bg-tile${call.background === choice ? ' is-on' : ''}`} onClick={() => pick(choice)} title={label}>
      {content}
      <span className="effect-name">{label}</span>
    </button>
  );
  return (
    <div className="stack" style={{ gap: 'var(--space-2)' }}>
      <strong className="call-panel-sub">{t('call.bg.title')}</strong>
      <div className="effect-grid" role="radiogroup" aria-label={t('call.bg.title')}>
        {tile('none', t('call.bg.none'), <Icon name="close" size={20} />)}
        {tile('blur', t('call.bg.blur'), <span className="bg-blur-icon" />)}
        {tile('blur-strong', t('call.bg.blurStrong'), <span className="bg-blur-icon strong" />)}
        {BACKGROUND_PRESETS.map((p) => tile(p, t(`call.bg.${p}`), <PresetThumb preset={p} />))}
        {tile('custom', t('call.bg.custom'), <Icon name="plus" size={20} />)}
      </div>
      <div className="row" style={{ gap: 'var(--space-2)' }}>
        <button type="button" className="btn btn-sm call-ghost-btn" onClick={() => input.current?.click()}>
          <Icon name="attach" size={16} /> {t('call.bg.upload')}
        </button>
        {call.background === 'custom' && (
          <button
            type="button"
            className="btn btn-sm call-ghost-btn"
            onClick={() => {
              void removeCustomBackground();
              pick('none');
            }}
          >
            {t('call.bg.removeCustom')}
          </button>
        )}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          setError(null);
          void calls.setCustomBackground(file).catch(() => setError(t('call.bg.badImage')));
        }}
      />
      {call.backgroundStatus === 'loading' && (
        <p className="call-panel-note" role="status">
          {t('call.bg.loading')}
        </p>
      )}
      {call.backgroundStatus === 'failed' && (
        <p className="call-panel-error" role="alert">
          {t('call.bg.failed')}
        </p>
      )}
      {error && (
        <p className="call-panel-error" role="alert">
          {error}
        </p>
      )}
      <p className="call-panel-note">{t('call.bg.privacy')}</p>
    </div>
  );
}

/** Drag the mini window anywhere; it stays inside the screen. */
function useDrag(initial: () => { x: number; y: number }) {
  const [pos, setPos] = useState(initial);
  const drag = useRef<{ dx: number; dy: number; moved: boolean } | null>(null);
  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    if (!drag.current) return;
    const w = e.currentTarget.offsetWidth;
    const h = e.currentTarget.offsetHeight;
    drag.current.moved = true;
    setPos({
      x: Math.min(Math.max(8, e.clientX - drag.current.dx), window.innerWidth - w - 8),
      y: Math.min(Math.max(8, e.clientY - drag.current.dy), window.innerHeight - h - 8),
    });
  };
  const onPointerUp = () => {
    drag.current = null;
  };
  // Re-clamped when the window changes size (rotation, resizing), so it never ends up off screen.
  const [, setViewport] = useState(0);
  useEffect(() => {
    const onResize = () => setViewport((n) => n + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const clamped = {
    x: Math.max(8, Math.min(pos.x, window.innerWidth - (window.innerWidth <= 600 ? 208 : 268))),
    y: Math.max(8, Math.min(pos.y, window.innerHeight - 120)),
  };
  return { pos: clamped, handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp } };
}

export function ActiveCall({ call, calls, status }: { call: CallView; calls: CallManager; status: string }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const navigate = useNavigate();
  const [mini, setMini] = useState(false);
  const [panel, setPanel] = useState<'devices' | 'effects' | null>(null);
  const remoteRef = useRef<HTMLVideoElement>(null);
  const video = call.media === 'video';
  const name = call.peer.displayName;
  const { pos, handlers } = useDrag(() => ({ x: window.innerWidth - 268, y: window.innerHeight - (video ? 236 : 132) }));
  const pipSupported = video && typeof document !== 'undefined' && 'pictureInPictureEnabled' in document && document.pictureInPictureEnabled;
  const phone = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
  const [facing, setFacing] = useState<'user' | 'environment'>('user');

  const openChat = () => {
    setMini(true);
    setPanel(null);
    navigate(`/messages/${call.conversationId}`);
  };
  const pip = () => {
    const el = remoteRef.current;
    if (!el) return;
    if (document.pictureInPictureElement) void document.exitPictureInPicture().catch(() => undefined);
    else void el.requestPictureInPicture().catch(() => undefined);
  };

  const remote = video && call.remoteStream ? (
    <CallVideo stream={call.remoteStream} className="call-remote" sinkId={call.speakerId} videoRef={remoteRef} />
  ) : (
    <>
      <div className="call-person">
        <UserAvatar userId={call.peer.id} name={name} large className="call-person-avatar" />
      </div>
      {/* Audio-only calls still need an element to play the other side. */}
      {call.remoteStream && <CallVideo stream={call.remoteStream} className="visually-hidden" sinkId={call.speakerId} videoRef={remoteRef} />}
    </>
  );

  if (mini) {
    return (
      <div className={`call-mini${video ? ' has-video' : ''}`} style={{ left: pos.x, top: pos.y }} role="dialog" aria-label={t('call.miniLabel', { name })} {...handlers}>
        {remote}
        <div className="call-mini-info">
          <strong className="truncate">{name}</strong>
          <span>{status}</span>
        </div>
        <div className="call-mini-actions">
          <RoundButton icon={call.muted ? 'micOff' : 'mic'} label={call.muted ? t('call.unmute') : t('call.mute')} pressed={call.muted} active={call.muted} onClick={() => calls.toggleMute()} />
          <RoundButton icon="maximize" label={t('call.expand')} onClick={() => setMini(false)} />
          <RoundButton icon="phoneOff" label={t('call.hangup')} danger onClick={() => calls.hangup()} />
        </div>
      </div>
    );
  }

  return (
    <div className={`call-overlay call-active ${video ? 'call-video' : ''}`} role="dialog" aria-label={name}>
      {remote}
      {video && call.localStream && !call.cameraOff && <CallVideo stream={call.localStream} muted className={`call-local${facing === 'environment' ? ' no-mirror' : ''}`} />}
      <div className="call-top">
        <button type="button" className="call-top-btn" aria-label={t('call.minimize')} title={t('call.minimize')} onClick={() => setMini(true)}>
          <Icon name="minimize" size={20} />
        </button>
        <div className="call-top-text">
          <p className="call-title">{name}</p>
          <p className="call-status" aria-live="polite">
            {status}
          </p>
        </div>
        {pipSupported && (
          <button type="button" className="call-top-btn" aria-label={t('call.pip')} title={t('call.pip')} onClick={pip}>
            <Icon name="pip" size={20} />
          </button>
        )}
      </div>
      {panel === 'devices' && <DevicePanel call={call} calls={calls} onClose={() => setPanel(null)} />}
      {panel === 'effects' && <EffectsPanel call={call} calls={calls} onClose={() => setPanel(null)} />}
      <div className="call-controls">
        <RoundButton icon={call.muted ? 'micOff' : 'mic'} label={call.muted ? t('call.unmute') : t('call.mute')} pressed={call.muted} active={call.muted} onClick={() => calls.toggleMute()} />
        {video && (
          <RoundButton icon={call.cameraOff ? 'videoOff' : 'video'} label={call.cameraOff ? t('call.cameraOn') : t('call.cameraOff')} pressed={call.cameraOff} active={call.cameraOff} onClick={() => calls.toggleCamera()} />
        )}
        {video && phone && (
          <RoundButton
            icon="flip"
            label={t('call.flip')}
            onClick={() => {
              const next = facing === 'user' ? 'environment' : 'user';
              setFacing(next);
              void calls.setCamera({ facing: next });
            }}
          />
        )}
        {video && <RoundButton icon="effects" label={t('call.effects.title')} active={panel === 'effects' || call.effect !== 'none'} onClick={() => setPanel(panel === 'effects' ? null : 'effects')} />}
        <RoundButton icon="settings" label={t('call.settings.title')} active={panel === 'devices'} onClick={() => setPanel(panel === 'devices' ? null : 'devices')} />
        <RoundButton icon="messages" label={t('call.chat')} onClick={openChat} />
        <RoundButton icon="phoneOff" label={t('call.hangup')} danger onClick={() => calls.hangup()} />
      </div>
      <p className="call-note">
        <Icon name="lock" size={12} /> {t('call.encryptedNote')}
      </p>
    </div>
  );
}
